/**
 * Campaign Feature Toggles
 *
 * This file contains feature flags for campaign-related functionality.
 * These toggles allow easy enabling/disabling of specific campaign features
 * without removing code, making it simple to reactivate features for future campaigns.
 *
 * To enable a feature for next year's campaign, simply change the flag to `true`.
 */

/**
 * Controls whether non-logged-in visitors can see Black Friday prices
 *
 * When `true`: Non-logged visitors can see prices for Black Friday products during active campaign
 * When `false`: Non-logged visitors cannot see prices (must log in to see pricing)
 *
 * Related files:
 * - src/views/shoppingCart/useCampaignInfo.js (campaignConfig.shouldDisplayNonLoggedPrices)
 * - src/utils/productUtils.js (getIsBlackFridayProduct function)
 * - src/components/ProductCard/index.jsx (price visibility logic)
 *
 * @type {boolean}
 */
export const ENABLE_NON_LOGGED_BLACK_FRIDAY_PRICES = true

/**
 * Controls whether the floating button appears during the campaign period
 *
 * When `true`: Floating button shows to non-logged users during and after campaign
 * When `false`: Floating button is hidden during the campaign (not shown at all)
 *
 * Note: The floating button is never shown to logged-in users regardless of this setting.
 *
 * Related files:
 * - src/layouts/shop/ShopLayoutFloatingButton.jsx (floating button component)
 * - src/views/shoppingCart/useCampaignInfo.js (campaignConfig.shouldShowFloatingButton)
 *
 * @type {boolean}
 */
export const ENABLE_FLOATING_BUTTON_DURING_CAMPAIGN = false

/**
 * Controls whether the Black Friday popup appears during the configured popup period
 *
 * When `true`: Black Friday popup shows once per browser session during popup window
 * When `false`: Popup is completely disabled (not shown at all)
 *
 * Popup timing is configured via environment variables:
 * - REACT_APP_BLACK_FRIDAY_POPUP_START_DATE
 * - REACT_APP_BLACK_FRIDAY_POPUP_END_DATE
 *
 * Related files:
 * - src/components/_modals/BlackFridayPopup.jsx (popup component)
 * - src/utils/dateUtils.js (isBlackFridayPopupActive function)
 * - src/App.js (popup trigger logic)
 *
 * @type {boolean}
 */
export const ENABLE_BLACK_FRIDAY_POPUP = true

/**
 * Controls whether the Black Friday mid-campaign popup appears during the configured popup period
 *
 * When `true`: Mid-campaign popup shows once per browser session during popup window
 * When `false`: Popup is completely disabled (not shown at all)
 *
 * This is the "don't miss out" mid-campaign marketing popup that appears later in the campaign.
 *
 * Popup timing is configured via environment variables:
 * - REACT_APP_BLACK_FRIDAY_MID_CAMPAIGN_POPUP_START_DATE
 * - REACT_APP_BLACK_FRIDAY_MID_CAMPAIGN_POPUP_END_DATE
 *
 * Related files:
 * - src/components/_modals/BlackFridayMidCampaignPopup.jsx (popup component)
 * - src/utils/dateUtils.js (isBlackFridayMidCampaignPopupActive function)
 * - src/App.js (popup trigger logic)
 *
 * @type {boolean}
 */
export const ENABLE_BLACK_FRIDAY_MID_CAMPAIGN_POPUP = false

/**
 * Controls whether the Black Friday last-minute popup appears during the configured popup period
 *
 * When `true`: Last-minute popup shows once per browser session during popup window
 * When `false`: Popup is completely disabled (not shown at all)
 *
 * This is the "last chance" final day marketing popup that appears on the campaign's last day.
 *
 * Popup timing is configured via environment variables:
 * - REACT_APP_BLACK_FRIDAY_LAST_MINUTE_POPUP_START_DATE
 * - REACT_APP_BLACK_FRIDAY_LAST_MINUTE_POPUP_END_DATE
 *
 * Related files:
 * - src/components/_modals/BlackFridayLastMinutePopup.jsx (popup component)
 * - src/utils/dateUtils.js (isBlackFridayLastMinutePopupActive function)
 * - src/App.js (popup trigger logic)
 *
 * @type {boolean}
 */
export const ENABLE_BLACK_FRIDAY_LAST_MINUTE_POPUP = true
